/**
 * Pricing Intelligence Section
 * Section 4: Optimal prices, elasticity curves, competitor comparison
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface PricingIntelligenceProps {
  data?: {
    recommendations?: Array<{
      planId: string;
      currentPrice: number;
      optimalPrice: number;
      recommendation: string;
      confidence: number;
    }>;
    elasticityCurves?: Array<{
      planId: string;
      curve: Array<{ price: number; demand: number }>;
    }>;
    competitorComparison?: Array<{
      planId: string;
      competitors: Array<{ name: string; price: number }>;
    }>;
  };
}

export function PricingIntelligence({ data }: PricingIntelligenceProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {data?.recommendations && data.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>AI Price Recommendations</CardTitle>
            <CardDescription>Optimal pricing based on market analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.recommendations.map((rec, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold">{rec.planId}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{rec.recommendation}</p>
                    </div>
                    <Badge variant="outline">Confidence: {rec.confidence}%</Badge>
                  </div>
                  <div className="flex gap-4 mt-2">
                    <div>
                      <span className="text-sm text-muted-foreground">Current</span>
                      <div className="font-semibold">{formatCurrency(rec.currentPrice)}</div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Optimal</span>
                      <div className="font-semibold text-green-600">
                        {formatCurrency(rec.optimalPrice)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data?.competitorComparison && data.competitorComparison.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Competitor Pricing Comparison</CardTitle>
            <CardDescription>Market positioning analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.competitorComparison.map((comp, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-2">{comp.planId}</h4>
                  <div className="space-y-1">
                    {comp.competitors.map((competitor, cIndex) => (
                      <div key={cIndex} className="flex justify-between text-sm">
                        <span>{competitor.name}</span>
                        <span className="font-medium">{formatCurrency(competitor.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
